import {
  CLIENTS_FOR_CLIENT_TYPE_SELECTION,
  PRESCREENER_CLIENT_TYPES,
  QUESTION_TYPES,
} from 'client-portal/utils/prescreener/constants';
import { tracked } from '@glimmer/tracking';
import Service, { service } from '@ember/service';

export default class PrescreenerValidationService extends Service {
  @service store;
  @service prescreener;

  currentClientType = PRESCREENER_CLIENT_TYPES.client;
  validationModels = {};
  answers = {};

  @tracked forceValidation = false;

  get acceptableClientTypes() {
    return CLIENTS_FOR_CLIENT_TYPE_SELECTION[this.currentClientType];
  }

  getValidatedAnswerModel(question, clientType = PRESCREENER_CLIENT_TYPES.client) {
    let { name } = question;

    let model = this.getCachedValidationModel(name, clientType);
    if (model) return model;

    const value = this.ensureAnswersFromValues(question, clientType);
    const validationModel = this.createValidationModelForQuestion(question, value, clientType);

    return this.createCachedValidationModel(name, clientType, validationModel);
  }

  createCachedValidationModel(name, clientType, validationModel) {
    if (!this.validationModels[name]) {
      this.validationModels[name] = {};
    }
    return (this.validationModels[name][clientType] = validationModel);
  }

  getCachedValidationModel(name, clientType) {
    if (this.validationModels[name]?.[clientType]) {
      return this.validationModels[name][clientType];
    }
    return null;
  }

  createValidationModelForQuestion(question, value, clientType = PRESCREENER_CLIENT_TYPES.client) {
    return this.store.createRecord('prescreener/answer-validation', {
      ...question,
      value,
      clientType,
    });
  }

  ensureAnswersFromValues(question, clientType = PRESCREENER_CLIENT_TYPES.client) {
    let { name } = question;
    const value = this.getInitialValue(question);
    if (value !== null) {
      this.answers[name] = clientType ? { [clientType]: value } : value;
    }
    return value;
  }

  updateAnswers(question, value, clientType = PRESCREENER_CLIENT_TYPES.client) {
    let { name } = question;

    if (!this.answers[name]) {
      this.answers[name] = {};
    }
    this.answers[name][clientType] = value;
  }

  getInitialValue(question) {
    if (question.type === QUESTION_TYPES.SELECT && question.options) {
      const selectedOption = this.#findSelectedOption(question.options);
      if (selectedOption) {
        return selectedOption.value;
      }
    }
    return null;
  }

  hasValue(inputObject) {
    return Object.values(inputObject).find(input => {
      if (typeof input === 'string') {
        return input.trim() !== '';
      }
      return input !== null && input !== undefined;
    });
  }

  get answersForAnswerModel() {
    return Object.entries(this.answers).reduce((acc, [name, answer]) => {
      if (this.hasValue(answer)) {
        acc = this.setAnswerToAccumulator(acc, name, answer);
      }
      return acc;
    }, {});
  }

  setAnswerToAccumulator(acc, name, answer) {
    acc[name] = Object.entries(answer).map(([type, value]) => ({ type, values: [].concat(value) }));
    return acc;
  }

  #findSelectedOption(options) {
    for (const option of options) {
      if (option.selected) {
        return option;
      }
      if (option.options) {
        const selected = this.#findSelectedOption(option.options);
        if (selected) return selected;
      }
    }
    return null;
  }

  updateAnswerModel(question, value, clientType = PRESCREENER_CLIENT_TYPES.client) {
    const validationModel = this.getValidatedAnswerModel(question, clientType);
    validationModel.set('value', value);

    this.updateAnswers(question, value, clientType);
  }

  validateAll() {
    const allAnswers = Object.values(this.validationModels);
    this.forceValidation = true;
    return this.validateAnswerModels(allAnswers);
  }

  validateCurrentCategory(currentCategory) {
    if (!currentCategory) return true;

    this.forceValidation = true;

    const categoryAnswers = this.getAnswersForCategory(currentCategory);
    return this.validateAnswerModels(categoryAnswers);
  }

  getAnswersForCategory(category) {
    return Object.values(this.validationModels).filter(clientAnswers => {
      return Object.values(clientAnswers).find(answer => answer.category === category);
    });
  }

  validateAnswerModels(answerModels) {
    for (const answerModel of answerModels) {
      if (answerModel.validations) {
        if (answerModel.validations.isInvalid) {
          return false;
        }
      } else {
        if (!this.validateAnswerModels(Object.values(answerModel))) {
          return false;
        }
      }
    }
    return true;
  }

  clearValidationModels() {
    this.validationModels = {};
    this.answers = {};
  }

  resetValidation() {
    this.forceValidation = false;
  }

  validateForClientType(newType) {
    this.currentClientType = newType;
    this.removeUnacceptableClientTypes();
  }

  removeUnacceptableClientTypes() {
    let acceptedClientTypes = this.acceptableClientTypes;

    Object.entries(this.validationModels).forEach(([questionName, clientValidationModels = {}]) => {
      let clientAnswers = this.answers[questionName];

      Object.entries(clientValidationModels).forEach(([clientType, validationModel]) => {
        if (!acceptedClientTypes.includes(clientType)) {
          if (validationModel) this.store.unloadRecord(validationModel);

          delete clientValidationModels?.[clientType];
          delete clientAnswers?.[clientType];
        }
      });
    });
  }
}
